import { useEffect, useRef } from "react";
/**
 * Das Tastaturmuster eines Menüs (`role="menu"`) — für alle drei Menüs dieser Anwendung:
 * das Zahnrad und das Konto in der Kopfleiste, das Überlaufmenü der unteren Leiste.
 *
 * WARUM ES DAS BRAUCHT: `role="menu"` ist kein Schmuck, sondern ein VERSPRECHEN. Wer einem
 * Bildschirmleser sagt „das hier ist ein Menü", verspricht damit das dazugehörige
 * Bedienmuster — Fokus wandert beim Aufklappen hinein, Pfeiltasten gehen von Eintrag zu
 * Eintrag, Home und End springen an die Enden, und nur EIN Eintrag liegt in der
 * Tabulatorreihe (roving tabindex). Bis zum 10.09.2026 trugen alle drei Menüs die Rolle und
 * hielten nichts davon; ein Tastaturbenutzer musste sich mit der Tabulatortaste durch sie
 * hindurcharbeiten wie durch eine gewöhnliche Liste, und der Bildschirmleser sagte ihm die
 * ganze Zeit etwas anderes.
 *
 * Escape gehört NICHT hierher: Es ist keine Frage des Menüs, sondern eine des Stapels —
 * siehe useEscapeEbene.ts. Ein Menü über einem überlagernden Rand soll bei Escape zugehen,
 * ohne den Rand mitzunehmen, und das kann nur eine Stelle entscheiden, die beide kennt.
 *
 * TAB dagegen schon, und zwar mit einem anderen Ausgang als Escape (WAI-ARIA, Muster
 * „Menu Button"): Die Tabulatortaste SCHLIESST das Menü und lässt den Fokus normal
 * weiterwandern — sie gibt ihn NICHT an den Knopf zurück. Der Unterschied ist die Absicht:
 * Escape heisst „ich wollte das doch nicht" und stellt den Zustand davor wieder her; Tab
 * heisst „ich bin hier fertig und gehe weiter". Bis zur Fixrunde 2 blieb das Menü bei Tab
 * offen stehen, während der Fokus schon woanders war — ein aufgeklapptes Menü, zu dem
 * niemand mehr gehört.
 *
 * Die Einträge werden bei JEDER Taste frisch aus dem DOM gelesen und nicht einmal beim
 * Öffnen eingesammelt: Ein Menü darf seinen Inhalt ändern, solange es offen steht (das
 * Überlaufmenü der unteren Leiste tut genau das, wenn sich die Leiste neu misst). Eine
 * eingefrorene Liste zeigte dann auf Einträge, die es nicht mehr gibt.
 */
/** Bedienbare Einträge in Dokumentreihenfolge. Abgeblendete zählen nicht mit. */
function eintraege(menu) {
    return Array.from(menu.querySelectorAll('[role="menuitem"]'))
        .filter((k) => !(k instanceof HTMLButtonElement && k.disabled)
        && k.getAttribute("aria-disabled") !== "true");
}
/**
 * Roving tabindex: Genau EIN Eintrag liegt in der Tabulatorreihe — der, auf dem man steht.
 * Alles andere erreicht man mit den Pfeiltasten. Ohne das würde die Tabulatortaste durch
 * alle Einträge laufen und damit dieselbe Bewegung zweimal anbieten (dieselbe Mechanik wie
 * an den Reitern der Kundenakte, siehe KundenAkte.tsx).
 */
function setzeTabreihe(alle, aktiv) {
    for (const k of alle)
        k.tabIndex = k === aktiv ? 0 : -1;
}
function gehZu(alle, ziel) {
    const eintrag = alle[ziel];
    if (eintrag === undefined)
        return;
    setzeTabreihe(alle, eintrag);
    eintrag.focus();
}
export function useMenuTasten(offen, menuRef, knopfRef, schliessen) {
    // Wie in useFocusTrap und useEscapeEbene: Der Rückruf kommt fast immer als
    // Inline-Funktion und darf den Effekt nicht bei jedem Neuzeichnen neu verdrahten.
    const schliessenRef = useRef(schliessen);
    schliessenRef.current = schliessen;
    useEffect(() => {
        if (!offen)
            return undefined;
        const menu = menuRef.current;
        if (menu === null)
            return undefined;
        /*
         * Beim Aufklappen wandert der Fokus auf den ERSTEN Eintrag. Das ist die Regel für ein
         * Menü, das über einen Knopf geöffnet wurde — anders als bei einem Dialog, in den man
         * hineinfällt, ist ein Menü genau dafür da, dass man sofort einen Eintrag wählt. Der
         * Rückweg ist Escape, und der gibt den Fokus an den Knopf zurück (siehe die Aufrufer).
         */
        const beimOeffnen = eintraege(menu);
        setzeTabreihe(beimOeffnen, beimOeffnen[0]);
        beimOeffnen[0]?.focus();
        function beiTaste(ereignis) {
            const alle = eintraege(menu);
            if (alle.length === 0)
                return;
            const jetzt = alle.findIndex((k) => k === document.activeElement);
            /*
             * Tab (und Shift+Tab) schliesst — OHNE preventDefault: Der Fokus soll seinen
             * gewohnten Weg gehen, das Menü nur nicht offen hinter ihm stehen bleiben.
             */
            if (ereignis.key === "Tab") {
                schliessenRef.current();
                return;
            }
            switch (ereignis.key) {
                // Umlauf an beiden Enden: Ein Menü ist ein Ring und keine Liste mit Anschlag —
                // wer unten weiterdrückt, will wieder oben herauskommen und nicht ins Leere.
                case "ArrowDown":
                    ereignis.preventDefault();
                    gehZu(alle, jetzt < 0 ? 0 : (jetzt + 1) % alle.length);
                    return;
                case "ArrowUp":
                    ereignis.preventDefault();
                    gehZu(alle, jetzt < 0 ? alle.length - 1 : (jetzt - 1 + alle.length) % alle.length);
                    return;
                case "Home":
                    ereignis.preventDefault();
                    gehZu(alle, 0);
                    return;
                case "End":
                    ereignis.preventDefault();
                    gehZu(alle, alle.length - 1);
                    return;
                default:
            }
        }
        /*
         * Der Schutz für alles, was NICHT über die Tastatur läuft: Verlässt der Fokus das Menü
         * und landet ausserhalb des Menüs UND ausserhalb seines Knopfes, gehört das Menü
         * niemandem mehr und geht zu. Deckt programmatische Fokuswechsel ab (ein Dialog, der
         * aufgeht; eine Ansicht, die ihren ersten Knopf fokussiert).
         *
         * Zwei Ausnahmen, beide bewusst:
         *  - `relatedTarget === null` heisst „der Fokus hat das DOKUMENT verlassen" (Fenster
         *    gewechselt, Adresszeile). Wer zurückkommt, soll sein Menü wiederfinden.
         *  - Der KNOPF selbst zählt nicht als aussen: Escape gibt den Fokus dorthin zurück, und
         *    ein Schliessen von hier aus wäre dabei nur ein zweiter Weg zum selben Ziel.
         */
        function beiFokusVerlust(ereignis) {
            const ziel = ereignis.relatedTarget;
            if (!(ziel instanceof Node))
                return;
            if (menu !== null && menu.contains(ziel))
                return;
            const knopf = knopfRef.current;
            if (knopf !== null && knopf.contains(ziel))
                return;
            schliessenRef.current();
        }
        menu.addEventListener("keydown", beiTaste);
        menu.addEventListener("focusout", beiFokusVerlust);
        return () => {
            menu.removeEventListener("keydown", beiTaste);
            menu.removeEventListener("focusout", beiFokusVerlust);
        };
        // menuRef und knopfRef sind über Neuzeichnungen hinweg stabile Objekte aus useRef und
        // können diesen Effekt gar nicht neu anstossen; `schliessen` liegt in einer Ref (siehe
        // oben). `offen` ist die einzige echte Bedingung.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offen]);
}
//# sourceMappingURL=useMenuTasten.js.map